import {RouterBridge, RouterBridgeInstance, ServerRouter, ServerRouterInstance} from "@/infrastructure/navigation";
import {proxy} from "valtio";

import {type LocationOption, LocationSearch, type LocationSearchInstance} from "@/features/locations";
import {GuestsStepper, type GuestsStepperInstance} from "@/features/rental/guests";
import {GuestsStepperConfig} from "@/features/rental/guests/guests-stepper.model";

import {SearchFilters} from "@/entities/rental";

import {INavigable, IServerNavigable} from "@/shared/contracts";
import {mergeQueryParams} from "@/shared/lib";
import {ScrollableDateRangePicker, type ScrollableDateRangePickerInstance} from "@/shared/models/form/datepickers";
import {RequiredValidator} from "@/shared/models/form/fields/validators";
import {AnalyticsService, type AnalyticsServiceInstance} from "@/shared/services/analytics/analytics.service";
import {routes} from "@/shared/utils";

/** Serializable date range for server→client transfer */
interface SerializableDateRange {
    startDate: string | null;
    endDate: string | null;
}

export interface Payload {
    guests?: GuestsStepperConfig;
    date?: SerializableDateRange;
    location?: LocationOption;
}
class SimpleSearchClass implements INavigable, IServerNavigable {
    locationSearch: LocationSearchInstance;
    guests: GuestsStepperInstance;
    dateRange: ScrollableDateRangePickerInstance;
    routerBridge: RouterBridgeInstance;
    serverRouter: ServerRouterInstance;
    private analytics: AnalyticsServiceInstance;

    constructor(payload?: Payload) {
        this.analytics = AnalyticsService();
        this.locationSearch = LocationSearch({
            validators: [new RequiredValidator("validation.enterLocation")],
            autoRedirect: false, // Handle redirect in submit based on location type
            initialSelectedOption: payload?.location,
        });
        this.guests = GuestsStepper(payload?.guests);

        this.dateRange = ScrollableDateRangePicker({
            initialValue: payload?.date,
            min: new Date(),
            initialMonthsCount: 3,
            loadMoreCount: 3,
        });
        this.routerBridge = RouterBridge();
        this.serverRouter = ServerRouter();
        this.locationSearch.events.on("select", (option) => {
            if (option.params) {
                this.applyDestinationParams(option.params);
            }
        });
    }

    private applyDestinationParams(destinationParams: string): void {
        const currentFilters = [
            this.locationSearch.getQueryParamSlug(),
            this.dateRange.getQueryParam("checkIn", "checkOut"),
            this.guests.getQueryParam(),
        ]
            .filter(Boolean)
            .join("&");

        const mergedParams = new URLSearchParams(mergeQueryParams(destinationParams, currentFilters));
        const adultsFromDest = mergedParams.get("adults");
        const childrenFromDest = mergedParams.get("children");
        const petsFromDest = mergedParams.get("pets");

        if (adultsFromDest) {
            this.guests.adults.setValue(parseInt(adultsFromDest, 10), true, true);
        }
        if (childrenFromDest) {
            this.guests.children.setValue(parseInt(childrenFromDest, 10), true, true);
        }
        if (petsFromDest) {
            this.guests.pets.setValue(parseInt(petsFromDest, 10), true, true);
        }
    }

    // Get query string for search URL

    get params(): SearchFilters {
        const destinationParams = this.locationSearch.selectedOption?.params || "";

        const currentFilters = [
            this.locationSearch.getQueryParamSlug(),
            this.dateRange.getQueryParam("checkIn", "checkOut"),
            this.guests.getQueryParam(),
        ]
            .filter(Boolean)
            .join("&");

        const mergedParams = new URLSearchParams(mergeQueryParams(destinationParams, currentFilters));
        return routes.search.parse(mergedParams);
    }

    onUrlChange(_pathname: string, searchParams: URLSearchParams): void {
        this.guests.setFromQueryParams(searchParams);
        this.dateRange.setFromQueryParams(searchParams, {start: "checkIn", end: "checkOut"});
    }

    /**
     * Validate and navigate based on location type
     * - Rental → rental detail page
     * - State/City → search page
     * @returns true if navigation was triggered, false if validation failed
     */
    private trackSearch(has_error: boolean): void {
        try {
            this.analytics.trackSimpleSearch({
                location_slug: has_error ? null : this.locationSearch.slug,
                has_dates: this.dateRange.hasValue,
                has_guests: this.guests.totalGuests > 1,
                has_error,
            });
        } catch (err) {
            console.warn("[SimpleSearch] trackSearch failed:", err);
        }
    }

    validate(): boolean {
        const isValid = true;
        this.trackSearch(!isValid);
        return isValid;
    }
    submit(): boolean {
        this.trackSearch(false);

        const selectedOption = this.locationSearch.selectedOption;

        // Navigate based on location type
        if (selectedOption?.type === "rental") {
            // Navigate to rental detail page with booking params
            const {adults, children, pets} = this.guests.value;
            const rentalUrl = routes.rental.detail.url(selectedOption.slug, {
                checkIn: this.dateRange.startDate ?? undefined,
                checkOut: this.dateRange.endDate ?? undefined,
                // Only include non-zero guest values
                ...(adults > 0 && {adults}),
                ...(children > 0 && {children}),
                ...(pets > 0 && {pets}),
            });
            this.serverRouter.navigate(rentalUrl);
        } else {
            // Navigate to search page
            const searchUrl = routes.search.url(this.params);
            this.serverRouter.navigate(searchUrl);
        }

        return true;
    }

    init(): void {
        this.dateRange.events.on("endSelect", () => {
            this.guests.openDropDown();
        });
    }
}

// Export type
export type SimpleSearchInstance = SimpleSearchClass;

export function SimpleSearch(payload?: Payload): SimpleSearchInstance {
    return proxy(new SimpleSearchClass(payload));
}
